// @ts-nocheck

import React, { useCallback, useEffect, useRef, cloneElement, useState } from 'react';
import styled from 'styled-components';

import { usePrevious } from '../../../hooks';
import Formatter from '../../../i18n/formatter';
import { __, getCurrency } from '../../../i18n/l10n';
import { SportIds } from '../../../types/sports';
import { panelStatuses } from '../constants';
import { PANEL_PADDING } from '../constants/style';

const DURATION_TO_HIDE_OVERLAY = 8000; // ms
const DURATION_BETWEEN_OVERLAY_PUSH = 300000; // ms --> 5min
const BUFFER_DURATION = 15000; // ms

const sportsPushPanelOnPeriodUpdate = [
  SportIds.SOCCER,
  SportIds.BASKETBALL,
  SportIds.BASEBALL,
  SportIds.ICEHOCKEY,
  SportIds.TENNIS,
  SportIds.HANDBALL,
  SportIds.RUGBY,
  SportIds.FOOTBALL,
  SportIds.SNOOKER,
  SportIds.TABLE_TENNIS,
  SportIds.VOLLEYBALL,
  SportIds.FIELD_HOCKEY,
  SportIds.BADMINTON,
  SportIds.BEACH_VOLLEY,
  SportIds.SQUASH,
  SportIds.BEACH_SOCCER,
  SportIds.RUGBY_LEAGUE,
  SportIds.RUGBY_7S,
  SportIds.RUGBY_LEAGUE_OLD,
  SportIds.RUGBY_7S_OLD,
  SportIds.PADEL,
];

const sportsPushPanelOnInterval = [
  SportIds.SOCCER,
  SportIds.BASEBALL,
  SportIds.ICEHOCKEY,
  SportIds.RUGBY,
  SportIds.FOOTBALL,
  SportIds.FIELD_HOCKEY,
  SportIds.RUGBY_LEAGUE,
  SportIds.RUGBY_7S,
  SportIds.RUGBY_LEAGUE_OLD,
  SportIds.RUGBY_7S_OLD,
];

const Overlay = styled.div`
  font-family: BettingBebasNeueRegular, sans-serif;
  z-index: 1;
  position: absolute;
  top: 0;
  right: 0;
  width: 40%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  box-sizing: border-box;
  padding: ${PANEL_PADDING}px;
  transition: transform 1000ms ease-out;
  color: #fff;
  @media (orientation: portrait) {
    display: none;
  }
`;

const Bankroll = styled.span`
  margin-left: 0.5em;
  float: right;
`;

const BankrollLabel = styled.span`
  float: left;
`;

const FixedContainer = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  padding: 0.5em;
  margin-bottom: 0.5em;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 2px;
`;

const EllipsisLabel = styled.label`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  line-height: 1.2;
`;

const Panel = (props) => {
  const {
    panelStatus,
    setPanelStatus,
    setScores,
    isUserRealMoney,
    userBankroll,
    period,
    sportId,
    gameScore,
    resetPanelContent,
  } = props;

  const [availableHeight, setAvailableHeight] = useState(null);

  const refItem = useRef(null);

  const onClickPanel = useCallback(() => {
    console.log('[IMMERSIVE] click on panel');
    if (panelStatus === panelStatuses.TEMPORARILY_VISIBLE) {
      console.log(`[IMMERSIVE] click on panel - setting panelStatus to ${panelStatuses.PINNED}`);
      setPanelStatus(panelStatuses.PINNED);
    }
  }, [panelStatus, setPanelStatus]);

  // ---------- Handling hide panel ----------------------
  const hidePanelTimeoutRef = useRef(null);

  useEffect(() => {
    if (panelStatus === panelStatuses.TEMPORARILY_VISIBLE) {
      console.log('[IMMERSIVE] Set timeout to dismiss the panel');
      hidePanelTimeoutRef.current = setTimeout(() => {
        hidePanelTimeoutRef.current = null;
        setPanelStatus(panelStatuses.HIDDEN);
      }, DURATION_TO_HIDE_OVERLAY);
    } else {
      if (hidePanelTimeoutRef.current) {
        clearTimeout(hidePanelTimeoutRef.current);
        hidePanelTimeoutRef.current = null;
      }
    }
  }, [panelStatus, setPanelStatus]);

  // ---------- Handling push on interval ----------------
  const pushPanelIntervalTimeoutRef = useRef(null);

  useEffect(() => {
    if (panelStatus === panelStatuses.HIDDEN && sportsPushPanelOnInterval.includes(sportId)) {
      if (!pushPanelIntervalTimeoutRef.current) {
        pushPanelIntervalTimeoutRef.current = setTimeout(() => {
          pushPanelIntervalTimeoutRef.current = null;
          console.log(
            `[IMMERSIVE] Handling push on interval, starting timeout to set panelStatus to ${panelStatuses.TEMPORARILY_VISIBLE}`,
          );
          resetPanelContent();
          setPanelStatus(panelStatuses.TEMPORARILY_VISIBLE);
        }, DURATION_BETWEEN_OVERLAY_PUSH);
      }
    } else {
      if (pushPanelIntervalTimeoutRef.current) {
        clearTimeout(pushPanelIntervalTimeoutRef.current);
        pushPanelIntervalTimeoutRef.current = null;
      }
    }
  }, [panelStatus, resetPanelContent, setPanelStatus, sportId]);

  // ---------- Handling push on period changes ----------
  const prevPeriod = usePrevious(period);
  const pushPanelPeriodTimeoutRef = useRef(null);

  useEffect(() => {
    if (panelStatus === panelStatuses.HIDDEN && sportsPushPanelOnPeriodUpdate.includes(sportId)) {
      if (prevPeriod !== undefined && period !== undefined && prevPeriod !== period && period !== 'ended') {
        if (!pushPanelPeriodTimeoutRef.current) {
          pushPanelPeriodTimeoutRef.current = setTimeout(() => {
            pushPanelPeriodTimeoutRef.current = null;
            console.log(
              `[IMMERSIVE] Handling push on period changes, starting timeout to set panelStatus to ${panelStatuses.TEMPORARILY_VISIBLE}`,
            );
            resetPanelContent();
            setPanelStatus(panelStatuses.TEMPORARILY_VISIBLE);
          }, BUFFER_DURATION);
        }
      }
    } else {
      if (pushPanelPeriodTimeoutRef.current) {
        clearTimeout(pushPanelPeriodTimeoutRef.current);
        pushPanelPeriodTimeoutRef.current = null;
      }
    }
  }, [prevPeriod, period, setPanelStatus, sportId, panelStatus, resetPanelContent]);

  // ---------- Handling push on setScores changes -------
  const prevSetScores = usePrevious(setScores);
  const pushPanelSetScoresTimeoutRef = useRef(null);

  useEffect(() => {
    if (panelStatus === panelStatuses.HIDDEN && sportId === SportIds.TENNIS) {
      if (prevSetScores !== undefined && setScores !== undefined && prevSetScores !== setScores) {
        const lastSpacePosition = setScores.lastIndexOf(' ');
        const lastSetScore = setScores.slice(lastSpacePosition + 1, setScores.length);
        const lastSetIndividualScores = lastSetScore.split(':').map((score) => parseInt(score, 10));
        const arraySum = (array) => array.reduce((a, b) => a + b, 0);
        const lastSetScoresSum = arraySum(lastSetIndividualScores);
        if (lastSetScoresSum !== 0 && lastSetScoresSum % 2 !== 0) {
          if (!pushPanelSetScoresTimeoutRef.current) {
            pushPanelSetScoresTimeoutRef.current = setTimeout(() => {
              console.log(
                `[IMMERSIVE] Handling push on setScores changes, starting timeout to set panelStatus to ${panelStatuses.TEMPORARILY_VISIBLE}`,
              );
              resetPanelContent();
              setPanelStatus(panelStatuses.TEMPORARILY_VISIBLE);
            }, BUFFER_DURATION);
          }
        }
      }
    } else {
      if (pushPanelSetScoresTimeoutRef.current) {
        clearTimeout(pushPanelSetScoresTimeoutRef.current);
        pushPanelSetScoresTimeoutRef.current = null;
      }
    }
  }, [panelStatus, prevSetScores, resetPanelContent, setPanelStatus, setScores, sportId]);

  // ---------- Handling push on gameScore changes -------
  const prevGameScore = usePrevious(gameScore);
  const pushPanelGameScoreTimeoutRef = useRef(null);

  useEffect(() => {
    if (panelStatus === panelStatuses.HIDDEN && sportId === SportIds.TENNIS) {
      if (prevGameScore !== undefined && gameScore !== undefined && prevGameScore !== gameScore) {
        const lastSpacePosition = setScores.lastIndexOf(' ');
        const lastSetScore = setScores.slice(lastSpacePosition + 1, setScores.length);

        if (lastSetScore === '6:6') {
          const lastIndividualScore = gameScore.split(':').map((score) => parseInt(score, 10));
          const arraySum = (array) => array.reduce((a, b) => a + b, 0);
          const lastGameScoresSum = arraySum(lastIndividualScore);

          if (lastGameScoresSum !== 0 && lastGameScoresSum % 6 === 0) {
            if (!pushPanelGameScoreTimeoutRef.current) {
              pushPanelGameScoreTimeoutRef.current = setTimeout(() => {
                console.log(
                  `[IMMERSIVE] Handling push on gameScore changes, starting timeout to set panelStatus to ${panelStatuses.TEMPORARILY_VISIBLE}`,
                );
                resetPanelContent();
                setPanelStatus(panelStatuses.TEMPORARILY_VISIBLE);
              }, BUFFER_DURATION);
            }
          }
        }
      }
    } else {
      if (pushPanelGameScoreTimeoutRef.current) {
        clearTimeout(pushPanelGameScoreTimeoutRef.current);
        pushPanelGameScoreTimeoutRef.current = null;
      }
    }
  }, [gameScore, panelStatus, prevGameScore, resetPanelContent, setPanelStatus, setScores, sportId]);

  // ---------- Clear all timeouts ! ---------------------
  useEffect(() => {
    return () => {
      if (pushPanelPeriodTimeoutRef.current) {
        clearTimeout(pushPanelPeriodTimeoutRef.current);
        pushPanelPeriodTimeoutRef.current = null;
      }

      if (pushPanelIntervalTimeoutRef.current) {
        clearTimeout(pushPanelIntervalTimeoutRef.current);
        pushPanelIntervalTimeoutRef.current = null;
      }

      if (hidePanelTimeoutRef.current) {
        clearTimeout(hidePanelTimeoutRef.current);
        hidePanelTimeoutRef.current = null;
      }

      if (pushPanelSetScoresTimeoutRef.current) {
        clearTimeout(pushPanelSetScoresTimeoutRef.current);
        pushPanelSetScoresTimeoutRef.current = null;
      }

      if (pushPanelGameScoreTimeoutRef.current) {
        clearTimeout(pushPanelGameScoreTimeoutRef.current);
        pushPanelGameScoreTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setAvailableHeight(refItem.current.getBoundingClientRect().height);
  }, [availableHeight]);

  return (
    <Overlay className="panel" onClick={onClickPanel} ref={refItem}>
      <FixedContainer>
        <EllipsisLabel>
          <BankrollLabel>{__('Balance').toUpperCase()}</BankrollLabel>
          <Bankroll>{Formatter.formatNumber(userBankroll / 100, isUserRealMoney ? getCurrency() : 'WAMS')}</Bankroll>
        </EllipsisLabel>
      </FixedContainer>
      <div>{cloneElement(props.children, { availableHeight })}</div>
    </Overlay>
  );
};

export default Panel;
